/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import '../startPage/startPage.css';
import '../b2bPage/b2bPage.css';
import ErrorPage from '../errorPage/ErrorPage';
import { Trans, useTranslation } from 'react-i18next';
import Spinner from '../../components/loaders/globalSpinner/Spinner';
import DCChargingLoader from '../../components/loaders/dcChargingLoader/DCChargingLoader';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { Container, Row } from 'react-bootstrap';
import { useInterval } from '../../hookServices/useInterval';
import OnlineStation from './getPower/OnlineStation';
import CongratsFail from './getPower/CongratsFail';
import Congrats from './getPower/Congrats';
import AskFeedbackPrompt from './AskFeedbackPrompt';
import { StationMediaCarousel } from '../../components/stationMediaCarousel/StationMediaCarousel';
import { api, useGetStationStatusQuery } from '../../redux/api';
import { useGetClientId } from '../../hookServices/clientId';
import { isEnergyCardInvoiceId } from '../../hookServices/energyCard';
import RestartStationButton from '../startPage/RestartStationButton';
import ServiceRequestModal from '../startPage/ServiceRequestModal';
import LeaveFeedbackModal from '../startPage/LeaveFeedbackModal';
import { paidPortsInHistory } from '../startPage/paidSession';
import RefundRequestModal from '../startPage/RefundRequestModal';
import DelayedStartWaitingPage from '../startPage/DelayedStartWaitingPage';
import { getDate, isDcPort } from '../../utils/utils';
import Box from '@mui/material/Box';
import ChargingPortCard from '../startPage/ChargingPortCard';
import StartPageTopOnlineStatus from '../startPage/StartPageTopOnlineStatus';
import StartPageStationNotes from '../startPage/StartPageStationNotes';
import { Button } from '../../shared/ui';
import OutageScheduleButton from '../../components/outageSchedule/OutageScheduleButton';
import buttonStyles from '../../shared/ui/button/button.module.css';
import { stationNameHasEmsBinding } from '../../utils/openEmsEvPortBattery';
import { resolveSessionCostPerKwt } from './sessionCostPerKwt';
import ReactGA from 'react-ga';
import { TELEGRAM_BOT } from '../../environment';

const FEEDBACK_DONT_ASK_KEY = 'feedbackDontAskAgain';
const FEEDBACK_ASK_AGAIN_MS = 30 * 24 * 60 * 60 * 1000;

function readDontAskFeedback() {
  try {
    const raw = localStorage.getItem(FEEDBACK_DONT_ASK_KEY);
    if (!raw) return false;
    // Older builds stored "1" for a permanent opt-out.
    if (raw === '1') return true;
    const until = Number(raw);
    return Number.isFinite(until) && until > Date.now();
  } catch {
    return false;
  }
}

function saveDontAskFeedback() {
  try {
    localStorage.setItem(FEEDBACK_DONT_ASK_KEY, String(Date.now() + FEEDBACK_ASK_AGAIN_MS));
  } catch {
    /* ignore */
  }
}

export default function ChargingPage({ fingerPrint }) {
  const [skip, setSkip] = useState(true);
  const [timer, setTimer] = useState(null);
  //todo do we need it?
  const [amountMoney, setAmountMoney] = useState('0.00');
  const [serviceRequestModalOpen, setServiceRequestModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [askFeedbackOpen, setAskFeedbackOpen] = useState(false);
  const feedbackPrompt1kWhShownRef = useRef(false);
  const feedbackPromptAfterStopShownRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { station, stationNumber, openFrom, closedFrom, isTwentyFourHour } = useOutletContext();
  const { endpoints } = api;
  const { clientId } = useGetClientId();
  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
  } = api.useGetHistoryDataQuery(clientId, { skip: !clientId });
  const isHistoryReady = !!clientId && !isHistoryLoading && (historyData !== undefined || isHistoryError);
  const canLeaveFeedback =
    isHistoryError || !isHistoryReady || paidPortsInHistory(historyData, [stationNumber]).length > 0;

  const { isLoading: isDeviceStartLoading } = endpoints.deviceStart.useQueryState({
    stationNumber,
    fingerPrint,
  });

  const {
    data: stationStatus,
    isError: isErrorStationsStatus,
    isLoading: isStationStatusLoading,
  } = useGetStationStatusQuery(
    { stationNumber, fingerPrint },
    {
      skip: skip,
      pollingInterval: 2000,
    }
  );

  const sessionCostPerKwt = resolveSessionCostPerKwt(
    stationStatus?.lastJob,
    station?.costPerKwt,
    historyData,
    stationNumber
  );
  const stationForSession = useMemo(
    () => (station == null ? station : { ...station, costPerKwt: sessionCostPerKwt }),
    [station, sessionCostPerKwt]
  );

  const chargedWtHRaw = stationStatus?.lastJob?.chargedWtH;
  const isZero = chargedWtHRaw === undefined || chargedWtHRaw === null || Number(chargedWtHRaw) === 0;
  let kWtCharged = Number(chargedWtHRaw || 0) / 1000;
  // Show 0.00 from the first poll, including before the meter reports energy.
  let chargeStatus = kWtCharged.toFixed(2);
  const lastInvoiceId = stationStatus?.lastJob?.invoiceId || '';
  const isBankRefundEligibleInvoice =
    Boolean(lastInvoiceId) && !isEnergyCardInvoiceId(lastInvoiceId) && !String(lastInvoiceId).includes('liquidBalance');
  const showRefundButton = stationStatus?.lastJob?.state === 'DONE' && isZero && isBankRefundEligibleInvoice;

  const [temperature, setTemperature] = useState(null);

  // Calculate Wh/km ratio based on temperature
  // temp < 0°C: 30 kWh/100km = 300 Wh/km
  // temp < 10°C: 25 kWh/100km = 250 Wh/km
  // temp >= 10°C: 20 kWh/100km = 200 Wh/km
  const getCarKwtKmRatio = () => {
    if (temperature === null) return 200; // default: 20 kWh/100km
    if (temperature < 0) return 300; // 30 kWh/100km
    if (temperature < 10) return 250; // 25 kWh/100km
    return 200; // 20 kWh/100km
  };

  let carKwtKmRatio = getCarKwtKmRatio();
  let voltage = Number(Math.round(stationStatus?.lastJob?.voltage));
  let kWtPower = Number(stationStatus?.lastJob?.powerWt) / 1000;
  const [stateStation, setStateStation] = useState(null);

  useInterval(() => {
    setStateStation(stationStatus?.lastJob?.state);
  }, 2000);

  const { t } = useTranslation();

  useEffect(() => {
    if (!isDeviceStartLoading) {
      setSkip(false);
    }
    // eslint-disable-next-line
  }, [isDeviceStartLoading]);

  // Fetch temperature from Open-Meteo API
  useEffect(() => {
    const fetchTemperature = async () => {
      if (!station?.latitude || !station?.longitude) return;
      try {
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${station.latitude}&longitude=${station.longitude}&current=temperature_2m`
        );
        const data = await response.json();
        if (data?.current?.temperature_2m !== undefined) {
          setTemperature(data.current.temperature_2m);
        }
      } catch (error) {
        console.error('Failed to fetch temperature:', error);
      }
    };
    fetchTemperature();
  }, [station?.latitude, station?.longitude]);

  //todo do we need this?
  const spendingMoney = () => {
    let kWtCharged = Number(stationStatus?.lastJob?.chargedWtH) * 1000;
    const kWtPeriod = Number(stationStatus?.lastJob?.periodWt) * 1000;
    const cost = sessionCostPerKwt * kWtPeriod;

    let amountHrn = Number(((cost * kWtCharged) / kWtPeriod).toFixed(2)) || '0.00';
    setAmountMoney(amountHrn.toString());
  };

  useEffect(() => {
    if (stationStatus?.lastJob?.state === 'DONE' || isErrorStationsStatus) {
      setSkip(true);
    }
  }, [stationStatus?.lastJob?.state, isErrorStationsStatus]);

  useEffect(() => {
    if (stationStatus?.lastJob?.leftS) {
      setTimer(getDate(stationStatus?.lastJob?.leftS));
    }
    // eslint-disable-next-line
  }, [stationStatus?.lastJob?.state]);

  useInterval(() => {
    if (stationStatus?.lastJob?.state === 'IN_PROGRESS') {
      spendingMoney();
      setTimer(getDate(stationStatus?.lastJob?.leftS));
    }
  }, 15000);

  // After the first full kWh, ask once to leave feedback (unless snoozed).
  useEffect(() => {
    if (feedbackPrompt1kWhShownRef.current) return;
    if (stationStatus?.lastJob?.state !== 'IN_PROGRESS') return;
    if (!(kWtCharged >= 1)) return;
    if (!canLeaveFeedback) return;
    if (readDontAskFeedback()) return;
    feedbackPrompt1kWhShownRef.current = true;
    setAskFeedbackOpen(true);
    ReactGA.event({
      category: 'feedback',
      action: 'ask prompt after 1 kWh',
      label: String(stationNumber || ''),
    });
  }, [stationStatus?.lastJob?.state, kWtCharged, stationNumber, canLeaveFeedback]);

  // After Stop (DONE), ask to leave feedback again (unless snoozed).
  useEffect(() => {
    if (feedbackPromptAfterStopShownRef.current) return;
    if (stationStatus?.lastJob?.state !== 'DONE') return;
    if (!canLeaveFeedback) return;
    if (readDontAskFeedback()) return;
    feedbackPromptAfterStopShownRef.current = true;
    setAskFeedbackOpen(true);
    ReactGA.event({
      category: 'feedback',
      action: 'ask prompt after stop',
      label: String(stationNumber || ''),
    });
  }, [stationStatus?.lastJob?.state, stationNumber, canLeaveFeedback]);

  // console.log(stationStatus)

  function openLeaveFeedback() {
    ReactGA.event({
      category: 'feedback',
      action: 'open leave feedback',
      label: String(stationNumber || ''),
    });
    setFeedbackModalOpen(true);
  }

  function openRefundRequest() {
    ReactGA.event({
      category: 'refund_request',
      action: 'open refund form',
      label: String(stationNumber || ''),
    });
    setRefundModalOpen(true);
  }

  // Deep link: /charging?station=N&refund=1 opens RefundRequestModal for lastJob.
  useEffect(() => {
    if (searchParams.get('refund') !== '1') return undefined;
    setRefundModalOpen(true);
    ReactGA.event({
      category: 'refund_request',
      action: 'open from url',
      label: String(stationNumber || ''),
    });
    const next = new URLSearchParams(searchParams);
    next.delete('refund');
    setSearchParams(next, { replace: true });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isErrorStationsStatus) {
    return (
      <ErrorPage centerOnScreen errorHeader={t('errorDevHeader')} startAgain={true} errorBody={t('errorDevBody')} />
    );
  }

  if (stationStatus?.lastJob?.state === 'FAILED') {
    return (
      <ErrorPage
        centerOnScreen
        errorHeader={t('errorStartFailedHeader')}
        errorBody={t('errorStartFailedBody')}
        stationNumber={stationNumber}
        startAgain={true}
      />
    );
  }

  // Check if this is a DC port with max power > 30 kW
  const isDCPort = isDcPort(station?.plugType, station?.maxPowerWt);
  const lastJobState = stationStatus?.lastJob?.state;
  const isWaitingForStart =
    !stationStatus || (lastJobState !== 'IN_PROGRESS' && lastJobState !== 'DONE' && lastJobState !== 'WAITING');
  const powerWt = Number(stationStatus?.lastJob?.powerWt);
  const powerShown = Number.isFinite(powerWt) && powerWt > 0;
  const socValue = Number(stationStatus?.lastJob?.soc);
  const socShown = Number.isFinite(socValue) && socValue > 0;
  // Reconnect steps stay up after the job is in progress until the car reports power or SoC.
  const showDcStartLoader = isDCPort && (isWaitingForStart || (lastJobState === 'IN_PROGRESS' && !powerShown && !socShown));

  if (showDcStartLoader || (!isDCPort && isWaitingForStart)) {
    if (showDcStartLoader) {
      const startedS = Number(stationStatus?.lastJob?.startedS);
      const sessionStartedAtMs = Number.isFinite(startedS) && startedS > 0 ? startedS * 1000 : null;
      return <DCChargingLoader sessionStartedAtMs={sessionStartedAtMs} />;
    }
    return <Spinner pageCentered />;
  }

  function portInfo(stationNumber, isTwentyFourHour, openFrom, closedFrom) {
    return (
      <>
        <div className="charging-port-card-wrap">
          <ChargingPortCard station={stationForSession} stationNumber={stationNumber} />
        </div>

        {!isTwentyFourHour && (
          <Row id="startPageTitleBox" className="justify-content-center mt-3 mb-3 pr-1 pl-1">
            <p className="delayedStartTime">
              <Trans i18nKey={'delayedInfo'}>
                Порт працює з {{ openFrom }} до
                {{ closedFrom }}
              </Trans>
            </p>
          </Row>
        )}
      </>
    );
  }

  const hasEmsLayout = stationNameHasEmsBinding(station?.name);

  return (
    <Container
      id="chargingPage"
      className={hasEmsLayout ? 'charging-page--ems-bound' : undefined}
      fluid="lg"
      style={{
        overflow: 'visible',
        marginTop: 0,
        maxWidth: hasEmsLayout ? undefined : '600px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      <StartPageTopOnlineStatus station={station} />
      <Box sx={{ m: '0% 0' }}>
        <div style={{ display: 'flex', flexFlow: 'column', gap: '0px' }}>
          {portInfo(stationNumber, isTwentyFourHour, openFrom, closedFrom)}

          {stationStatus?.lastJob?.state === 'WAITING' && (
            <DelayedStartWaitingPage leftTimeToStartS={stationStatus?.lastJob?.leftTimeToStartS} />
          )}
          {stationStatus?.lastJob?.state === 'IN_PROGRESS' && (
            <OnlineStation
              station={stationForSession}
              stationStatus={stationStatus}
              // todo pass only station and stationStatus
              periodKwh={stationStatus.lastJob?.periodKwt}
              carKwtKmRatio={carKwtKmRatio}
              hasActualKmRatio={temperature !== null}
              kWhCharged={kWtCharged}
              costPerKwt={sessionCostPerKwt}
              voltage={voltage}
              kWtPower={kWtPower}
              chargeStatus={chargeStatus}
              onlineStatus={stationStatus?.lastJob?.deviceOnline}
              timer={timer}
              isZero={isZero}
              lastJob={stationStatus?.lastJob}
              amountMoney={amountMoney}
              type={stationStatus?.lastJob?.type}
            />
          )}
          {(stationStatus?.lastJob?.state === 'IN_PROGRESS' || stationStatus?.lastJob?.state === 'WAITING') && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                type="button"
                variant="outline"
                className={buttonStyles.buttonPhoneLabel}
                style={{
                  display: 'flex',
                  gap: '12px',
                  margin: '25px auto 0',
                  width: '86%',
                }}
                onClick={() => {
                  ReactGA.event({
                    category: 'contacts',
                    action: 'notify when finish',
                  });
                  window.open(
                    `https://t.me/${TELEGRAM_BOT}?start=${encodeURIComponent(clientId || 'web')}-finish_${stationNumber || ''}`,
                    '_blank'
                  );
                }}
              >
                <span>{t('notifyWhenFinish')}</span>
              </Button>
            </div>
          )}
          {stationStatus?.lastJob?.state === 'DONE' && !stationStatus?.lastJob?.stopReason?.includes('WARN. Power') && (
            <Congrats
              kWtCharged={kWtCharged}
              carKwtKmRatio={carKwtKmRatio}
              chargeStatus={chargeStatus}
              stationNumber={stationNumber}
              amountMoney={amountMoney}
              station={stationForSession}
              hideUah={isEnergyCardInvoiceId(stationStatus?.lastJob?.invoiceId)}
              ocppStopReason={stationStatus?.lastJob?.ocppStopReason}
              ocppErrorCode={stationStatus?.lastJob?.ocppErrorCode}
              ocppErrorInfo={stationStatus?.lastJob?.ocppErrorInfo}
              ocppVendorErrorCode={stationStatus?.lastJob?.ocppVendorErrorCode}
            />
          )}
          {stationStatus?.lastJob?.state === 'DONE' && stationStatus?.lastJob?.stopReason?.includes('WARN. Power') && (
            <CongratsFail
              kWtCharged={kWtCharged}
              carKwtKmRatio={carKwtKmRatio}
              chargeStatus={chargeStatus}
              stationNumber={stationNumber}
              amountMoney={amountMoney}
              ocppStopReason={stationStatus?.lastJob?.ocppStopReason}
              ocppErrorCode={stationStatus?.lastJob?.ocppErrorCode}
              ocppErrorInfo={stationStatus?.lastJob?.ocppErrorInfo}
              ocppVendorErrorCode={stationStatus?.lastJob?.ocppVendorErrorCode}
            />
          )}
          <StartPageStationNotes station={station} />
          {station?.plugshareLink && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                link={station?.plugshareLink}
                variant="outline"
                className={buttonStyles.buttonPhoneLabel}
                style={{
                  display: 'flex',
                  gap: '12px',
                  margin: '25px auto',
                  width: '86%',
                }}
              >
                <span>Plugshare</span>
              </Button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              link={`https://waze.com/ul?ll=${station.latitude},${station.longitude}&navigate=yes`}
              variant="outline"
              className={buttonStyles.buttonPhoneLabel}
              style={{
                display: 'flex',
                gap: '12px',
                margin: '25px auto',
                width: '86%',
              }}
            >
              <span>{t('route')}</span>
            </Button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <OutageScheduleButton
              latitude={station?.latitude}
              longitude={station?.longitude}
              stationNumber={stationNumber}
              className={buttonStyles.buttonPhoneLabel}
              labelStyle={null}
              style={{
                display: 'flex',
                gap: '12px',
                margin: '25px auto',
                width: '86%',
              }}
            />
          </div>
        </div>
      </Box>
      <StationMediaCarousel
        stationNumber={stationNumber}
        protocolType={station?.protocolType}
        webcamUrl={station?.webcamUrl}
      />
      <Box className="start-page-footer-actions" sx={{ margin: '20px auto 0' }}>
        {showRefundButton && (
          <Button
            type="button"
            variant="outline"
            className={buttonStyles.buttonPhoneLabel}
            style={{
              display: 'flex',
              gap: '12px',
              margin: '0 auto',
              width: '86%',
            }}
            onClick={openRefundRequest}
          >
            <span>{t('refundForm.button')}</span>
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          className={buttonStyles.buttonPhoneLabel}
          style={{
            display: 'flex',
            gap: '12px',
            margin: '0 auto',
            width: '86%',
          }}
          onClick={openLeaveFeedback}
        >
          <span>{t('feedback.leave')}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          className={buttonStyles.buttonPhoneLabel}
          style={{
            display: 'flex',
            gap: '12px',
            margin: '0 auto',
            width: '86%',
          }}
          onClick={() => setServiceRequestModalOpen(true)}
        >
          <span>{t('serviceRequest')}</span>
        </Button>
        <RestartStationButton
          station={station}
          stationNumber={stationNumber}
          historyData={historyData}
          isHistoryReady={isHistoryReady}
        />
      </Box>
      <ServiceRequestModal
        modalOpen={serviceRequestModalOpen}
        setModalOpen={setServiceRequestModalOpen}
        station={station}
        stationNumber={stationNumber}
        clientId={clientId}
      />
      <LeaveFeedbackModal
        modalOpen={feedbackModalOpen}
        setModalOpen={setFeedbackModalOpen}
        station={station}
        stationNumber={stationNumber}
        clientId={clientId}
        canLeaveFeedback={canLeaveFeedback}
      />
      <RefundRequestModal
        modalOpen={refundModalOpen}
        setModalOpen={setRefundModalOpen}
        stationNumber={stationNumber}
        clientId={clientId}
        invoiceId={lastInvoiceId}
        jobId={stationStatus?.lastJob?.id}
      />
      <AskFeedbackPrompt
        modalOpen={askFeedbackOpen}
        setModalOpen={setAskFeedbackOpen}
        onLeaveFeedback={openLeaveFeedback}
        onDontAskAgain={saveDontAskFeedback}
      />
      <style>{`
        @keyframes fade-animation {
          0% {
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.3;
          }
        }
      `}</style>
    </Container>
  );
}
